// One built chart, drawn.
//
// It is `ChartCard` and nothing else: the same component, `ChartSpec` and
// `ChartRenderState` the authored dashboard page uses (one render path, no
// second renderer to drift). Everything this wrapper adds sits AROUND the card:
// the annotation overlay, and (in the canvas) the selection and drag affordances.
//
// The mark set arrives as a PROP, not a fetch of its own: the backend returns
// an annotated tile's mark query on the same response, in the same per-chart
// state map, settled by the same Pusher event, charged to the same budget, and
// keyed by an id the server stated.
import { useMemo } from "react";

import type { ChartSpec, DatasetSpec, FilterState } from "@bi/shared/types";

import { annotationMarks, annotationsOf } from "../../lib/annotation-marks";
import type { ChartRenderState } from "../../lib/bi-query";
import { bucketsFromResult } from "../../lib/chart-result-adapter";
import { sizeClass } from "../../lib/dashboard-layout";
import { useDebounced, useStudioPreview } from "../../lib/studio-query";
import { ChartCard } from "../chart-card";
import { AnnotationLayer } from "./annotation-layer";

const NO_CONSTRAINTS: never[] = [];

export interface StudioTileProps {
  chart: ChartSpec;
  state: ChartRenderState;
  /** This tile's mark set, when it declares annotations. `null` means it
   *  declares none; an `error` state means the marks failed and the tile says
   *  so, rather than drawing a bare line that looks complete. */
  annotationState?: ChartRenderState | null;
  columnSpan: number;
  width: number;
  dataset: DatasetSpec | undefined;
  filters: FilterState;
  employeeEmail: string;
  /** Why this tile is empty, when the caller knows (`lib/tile-empty.ts`). A
   *  blank panel saying "No data for this filter" is true of every empty chart
   *  and therefore says nothing, and on a per-experiment board the three real
   *  reasons mean completely different things. */
  emptyExplanation?: string;
  onRetry: () => void;
}

export function StudioTile({
  chart,
  state,
  annotationState = null,
  columnSpan,
  width,
  dataset,
  emptyExplanation,
  onRetry,
}: StudioTileProps) {
  const annotations = annotationsOf(chart);

  const markSet = useMemo(() => {
    if (!annotations || state.status !== "ready") return null;
    if (!annotationState || annotationState.status !== "ready") return null;
    const buckets = bucketsFromResult(chart, state.result);
    // A bar chart CENTRES each bucket in its own slice while a line puts it on
    // the slice boundary; using one mapping for both puts a deploy mark half a
    // bar away from the day it happened.
    const mode = chart.viz_type === "echarts_timeseries_bar" ? "step" : "point";
    return annotationMarks(annotations, annotationState.result, buckets, mode);
  }, [annotations, chart, state, annotationState]);

  const marksFailed = Boolean(annotations) && annotationState?.status === "error";

  return (
    <div className="bi-studio-tile-body" style={{ gridColumn: `span ${columnSpan}` }}>
      <ChartCard
        chart={chart}
        state={state}
        columnSpan={12}
        sizeClass={sizeClass({ width })}
        inactiveFilterCount={0}
        authoredConstraints={NO_CONSTRAINTS}
        ignoresDateRange={false}
        dataset={dataset}
        emptyExplanation={emptyExplanation}
        onRetry={onRetry}
      />
      {markSet ? (
        <AnnotationLayer marks={markSet.marks} outsideCount={markSet.outsideCount} />
      ) : null}
      {marksFailed ? (
        // Said out loud. A time series whose marks failed looks exactly like one
        // that has no events in the window, and the two mean opposite things.
        <p className="bi-card__note">
          The event marks for this chart could not be loaded, so the line is shown without them.
        </p>
      ) : null}
    </div>
  );
}

/**
 * A tile drawing its OWN spec through the preview route — the canvas's tile.
 *
 * Every tile on a canvas previews itself rather than the page fanning out one
 * saved-dashboard query, and that is what makes the editor work before the
 * first save: there is no row to query, and half the tiles on screen are
 * usually a spec nobody has stored. The saved dashboard PAGE is the other case
 * and takes the other path (`useStudioDashboardCharts`), which is one request
 * for the whole board.
 *
 * The debounce is the affordability half. A preview is a real request against
 * the reader's daily budget plus a BigQuery dry run, and an editor that fires
 * one per keystroke spends a person's whole budget renaming a chart.
 */
export function StudioPreviewTile(
  props: Omit<StudioTileProps, "state" | "annotationState" | "onRetry">,
) {
  const settled = useDebounced(props.chart);
  const { state, annotation, retry } = useStudioPreview(
    settled,
    props.filters,
    props.employeeEmail,
  );
  return (
    <StudioTile
      {...props}
      chart={settled}
      state={state}
      annotationState={annotation}
      onRetry={retry}
    />
  );
}
